import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/apiClient';

type UserRow = {
  id: string;
  email: string;
  role: 'admin' | 'user';
  createdAt: string;
};

const UsersSettingsPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ email: '', password: '', role: 'user' as 'admin' | 'user' });
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ users: UserRow[] }>('/api/admin/users');
      setUsers(data.users);
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void load();
  }, [isAdmin, load, navigate]);

  const addUser = async () => {
    try {
      await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setAddOpen(false);
      setForm({ email: '', password: '', role: 'user' });
      setMessage({ type: 'success', text: `Invited ${form.email}` });
      void load();
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Could not add user' });
    }
  };

  const deleteUser = async (row: UserRow) => {
    if (!window.confirm(`Remove ${row.email}? This deletes their settings and tokens.`)) {
      return;
    }
    try {
      await apiFetch(`/api/admin/users/${row.id}`, { method: 'DELETE' });
      setMessage({ type: 'success', text: `Removed ${row.email}` });
      void load();
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Delete failed' });
    }
  };

  const resetPassword = async () => {
    if (!resetOpen) {
      return;
    }
    try {
      await apiFetch(`/api/admin/users/${resetOpen.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: newPassword }),
      });
      setResetOpen(null);
      setNewPassword('');
      setMessage({ type: 'success', text: `Password updated for ${resetOpen.email}` });
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Reset failed' });
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <PeopleOutlinedIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={800}>
            Users
          </Typography>
        </Stack>
        <Typography sx={{ opacity: 0.9 }}>
          Invite-only access — add team members here. They sign in with the email and password you set.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={2}>
        <Button variant="contained" startIcon={<PersonAddOutlinedIcon />} onClick={() => setAddOpen(true)}>
          Add user
        </Button>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary" py={2} textAlign="center">
                      Loading…
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                users.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.email}</Typography>
                      {row.id === user?.id && (
                        <Chip size="small" label="You" sx={{ ml: 1 }} color="primary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.role}
                        color={row.role === 'admin' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Reset password"
                        onClick={() => {
                          setResetOpen(row);
                          setNewPassword('');
                        }}
                      >
                        <KeyOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete user"
                        disabled={row.id === user?.id}
                        onClick={() => void deleteUser(row)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Temporary password"
              type="password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              helperText="At least 8 characters — share securely with the invitee"
            />
            <TextField
              select
              label="Role"
              fullWidth
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as 'admin' | 'user' }))}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void addUser()} disabled={!form.email || form.password.length < 8}>
            Add user
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetOpen)} onClose={() => setResetOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for {resetOpen?.email}
          </Typography>
          <TextField
            label="New password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={newPassword.length < 8} onClick={() => void resetPassword()}>
            Update password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersSettingsPage;
